import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import type { IRootState } from '@store/slices';
import type { SiteId, TaskId, TaskStatus } from '@interfaces/IScheduler';
import { useCurrentActor } from '@hooks/useCurrentActor';
import { useSchedulerActions, useSchedulerStale, useSchedulerTask } from './useSchedulerStore';
import { useTaskStatusPersist } from './useTaskStatusPersist';

// ---------------------------------------------------------------------------
// useTaskLifecycleActions — the task's PLAN-status transitions, shared by the
// drawer's header status pill and the Gantt row menu.
//
// Since the 2026-09-17 task-commit design there are three statuses and one
// verb: `setStatus`. `done` is a human decision (invariant 6) — reaching 100 %
// does not make a task done, and marking it done also completes the Trusstor
// group of a committed field task, which the server does. `blocked` is an
// UNCOMMITTED-only state (blocking a promise is an issue, not a status), which
// the reducer and the server both enforce.
//
// Persist reads the FRESH store post-dispatch (never the pre-dispatch
// snapshot): a gated dispatch (the checklist gate on `done`) may not have gone
// through at all, so we never persist blind.
//
// STALE GUARD: while the loaded schedule is behind the upstream version the
// reducer already freezes data edits, and a stale base must also never fire the
// PUT — so every action here is a hard no-op while stale.
// ---------------------------------------------------------------------------

export interface ITaskLifecycleActions {
  /** Mark done, block, or reopen/unblock (→ `planned`). */
  setStatus: (target: TaskStatus) => void;
}

export function useTaskLifecycleActions(
  siteId: SiteId,
  taskId: TaskId,
  scheduleNumber?: string,
  /** Told when the server refuses a done for an open checklist. */
  onChecklistRefused?: () => void,
): ITaskLifecycleActions {
  const task = useSchedulerTask(siteId, taskId);
  const actor = useCurrentActor();
  const stale = useSchedulerStale(siteId);
  const { taskStatusSet } = useSchedulerActions();
  const store = useStore<IRootState>();
  const persist = useTaskStatusPersist(siteId, scheduleNumber, taskId, onChecklistRefused);

  const setStatus = useCallback(
    (target: TaskStatus) => {
      if (stale || !task || target === task.status) return;
      taskStatusSet({
        siteId,
        taskId,
        status: target,
        activityId: `act-${crypto.randomUUID()}`,
        at: new Date().toISOString(),
        by: actor.id,
      });
      // Only persist what actually landed: the reducer refuses a blocked-while-
      // committed flip and a `done` the checklist still gates.
      const fresh = store.getState().scheduler.bySite[siteId]?.tasks.find((t) => t.id === taskId);
      if (fresh?.status === target) persist(target);
    },
    [stale, task, taskStatusSet, siteId, taskId, store, persist, actor.id],
  );

  return useMemo(() => ({ setStatus }), [setStatus]);
}
